// The PINNED dice CONTRACT the frontend consumes (Phase 2 / Goal A frontend-split, SC-57553/57555).
//
// Full provably-fair verify = RGS derivation (SDK: commitment/deriveRandoms/intN — STAYS in the SDK) + ENGINE
// resolve (the money-math — MOVED to the published @blocklabsdev/rgs-contracts package). This module is the
// single import surface the game uses for the ENGINE truth, the embedded pinned hash, the D11 version gate and
// the FUN boot self-check. The engine-resolve is `diceResolve` from @blocklabsdev/rgs-contracts (git-dep,
// v0.1.0) — NOT a local src/engine mirror and NOT a vendored copy. The frontend owns ZERO outcome/money math:
// every settled/verified number is the contract's, never a formula re-implemented here.

import pinnedHash from "./pinned-hash.json";

// ---- ENGINE money-math, sourced from CONTRACTS (published package) ----
// diceResolve(k, betCredits, sel) -> { k, win, winCredits, multiplierPpm, winCount }. This is the ONLY
// outcome/money computation the frontend performs, and it is the contract's. `roll = k/100` is PRESENTATION and
// is computed by the caller (the contract resolve returns money-math only, no roll).
export { diceResolve as resolve } from "@blocklabsdev/rgs-contracts";

// D11 version gate: exact-hash equality, fail closed (an empty hash never matches). From the package.
export { isSameContract } from "@blocklabsdev/rgs-contracts";

// ---- player selection type (frontend-owned; the contract takes the same shape: Sel{Mode,Target,Lo,Hi}) ----
export type Mode = "under" | "over" | "between" | "outside";
/** Player selection (4 modes, matching the Go engine's Sel{Mode,Target,Lo,Hi}). under/over carry a single
 *  `target` (hundredths ∈ [1, 9999], 4950 = 49.50); between/outside carry a half-open band [lo, hi) (hundredths).
 *  Only the two fields meaningful for the mode are set — the contract's Go engine marshals the rest `omitempty`. */
export interface Sel {
  mode: Mode;
  target?: number;
  lo?: number;
  hi?: number;
}

// ---- PINNED spec constants (from the pinned dice/game-spec.json — NOT money-math) ----
// RESOLUTION = the IntN(RESOLUTION) roll space (k ∈ [0,10000)). RANDOMS_PER_STEP = how many uint64 the RGS
// derives per round (1 that IntN(10000) consumes + 1 of MARGIN, sc-53956). They drive the SDK derivation
// (deriveRandoms(..., RANDOMS_PER_STEP)) and the IntN draw (intN(randoms, RESOLUTION)); they are pinned
// alongside the contract hash and re-pinned only when the git-dep tag is bumped.
export const RESOLUTION = 10_000;
export const RANDOMS_PER_STEP = 2;

// The embedded, PINNED dice contract hash — the exact contract THIS bundle was built against (build constant).
// The FUN boot self-check compares it to the same-origin manifest; scripts/check-contract-pin.mjs fails the
// build if it drifts from the live @blocklabsdev/rgs-contracts hash.
export const PINNED_DICE_CONTRACT_HASH: string = pinnedHash.dice;
/** The earlier identities of the same v1 contract the boot also accepts while gateways move to v0.10.0 (expand/contract,
 *  pinned-hash.json "transition"; SC-67714). Empty once the transition is over. */
export const DICE_PREDECESSOR_HASHES: readonly string[] = pinnedHash.transition.dice;
/** The math identity of a pf-ticket-v1 Dice round (its ticket's mathId) and the PF protocol contract (its pfProtocolId):
 *  a ticket round is verified by this build only when it was played under exactly these (src/pf/ticket-verify.ts). */
export const PINNED_DICE_V2_MATH_ID: string = pinnedHash["dice-v2"];
/** Every identity a ticket's mathId may name for that SAME math: the pinned one, then its listed predecessors while
 *  runtimes move to v0.10.0 (pinned-hash.json "transition"; SC-67714). Just the pinned one once the transition is over. */
export const DICE_V2_MATH_IDS: readonly string[] = [PINNED_DICE_V2_MATH_ID, ...pinnedHash.transition["dice-v2"]];
export const PINNED_PF_PROTOCOL_ID: string = pinnedHash["pf-ticket-v1"];

// ---- D11 FUN boot self-check (browser-safe; fail closed) ----
export { evaluateSelfCheck, selfCheckContract } from "./self-check.mjs";
export type { SelfCheckResult } from "./self-check.mjs";

// NOTE: the win-chance ↔ multiplier ↔ target/band PRESENTATION math lives in the vendored CoinPoker client
// (public/vendor/coinpoker-dice/js/the-handles.js) now, not here — the operator (src/main.ts) only maps the vendor's
// frozen round → Sel and relays the server's settled money. The old TS presentation helpers (targetFromChance,
// selectionMultiplierPpm, winPayoutMinor, chanceFromMultiplier) went with the retired src/ui/ DOM client.
